/**
 * Ollama API helper for vocabulary enrichment.
 *
 * This module provides a client for invoking an Ollama-compatible LLM API
 * (such as the one in nahooray/ollamaclient) to generate vocabulary
 * definitions, example sentences, and related words.
 *
 * === Integration with nahooray/ollamaclient ===
 *
 * The ollamaclient project exposes a REST API that wraps Ollama's local LLM
 * inference. To use it for vocabulary enrichment:
 *
 * 1. Run the ollamaclient server (default: http://localhost:11434)
 * 2. Set the REACT_APP_OLLAMA_API_BASE environment variable to the server URL
 * 3. Call the functions below from your React components
 *
 * Example .env:
 *   REACT_APP_OLLAMA_API_BASE=http://localhost:11434
 *
 * === Approaches for iPhone without a server ===
 *
 * Since Ollama requires a server process, here are approaches to use LLM
 * features on iPhone without a separate server:
 *
 * 1. **On-device CoreML models**: Use Apple's CoreML framework via a
 *    Capacitor/React Native bridge to run small language models directly
 *    on-device (e.g., distilled models converted to CoreML format).
 *
 * 2. **WebLLM / ONNX Runtime Web**: Run quantized LLM models in the browser
 *    using WebGPU or WASM via libraries like web-llm or onnxruntime-web.
 *    This works in a PWA on Safari with no server needed.
 *
 * 3. **Pre-generated enrichment data**: Generate definitions and example
 *    sentences at build time using Ollama and bundle them with the app.
 *    The app can store this data in IndexedDB for offline access.
 *
 * 4. **Optional cloud API fallback**: When network is available, call a
 *    cloud-hosted LLM API (OpenAI, Anthropic, etc.) and cache results
 *    locally in IndexedDB for offline use.
 */

import {getAiApiBaseUrl} from './aiApiConfig';

const OLLAMA_API_BASE = process.env.REACT_APP_OLLAMA_API_BASE || 'http://localhost:11434';
const DEFAULT_MODEL = process.env.REACT_APP_OLLAMA_MODEL || 'gemma3:1b';

// Resolve the AI host for generation calls. Prefers the host configured in
// the app (same one the availability probe checks), falling back to a direct
// Ollama at REACT_APP_OLLAMA_API_BASE / localhost:11434 for legacy setups.
function resolveApiBases() {
  const bases = [];
  const configured = getAiApiBaseUrl();
  if (configured) bases.push(configured.replace(/\/+$/, ''));
  if (!bases.includes(OLLAMA_API_BASE)) bases.push(OLLAMA_API_BASE);
  return bases;
}

/**
 * Send a generate request to the Ollama API.
 *
 * @param {object} options
 * @param {string} options.prompt - The prompt to send
 * @param {string} [options.model] - Model name (default: llama3)
 * @param {AbortSignal} [options.signal] - Optional abort signal
 * @returns {Promise<string>} The generated text response
 */
export async function ollamaGenerate({prompt, model, signal}) {
  let lastError = null;

  for (const base of resolveApiBases()) {
    let response;
    try {
      response = await fetch(`${base}/api/generate`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          model: model || DEFAULT_MODEL,
          prompt,
          stream: false,
        }),
        signal,
      });
    } catch (error) {
      lastError = error;
      if (signal && signal.aborted) throw error;
      continue;
    }

    if (!response.ok) {
      lastError = new Error(`Ollama API error: ${response.status}`);
      continue;
    }

    // API returns NDJSON (one JSON object per line) even with stream:false.
    const text = await response.text();
    let fullResponse = '';
    for (const line of text.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        const obj = JSON.parse(trimmed);
        if (obj.response) fullResponse += obj.response;
      } catch (_) {/* skip malformed lines */}
    }
    return fullResponse;
  }

  throw lastError || new Error('Ollama API unreachable.');
}

/**
 * Generate a definition and example sentence for a vocabulary word.
 *
 * @param {object} options
 * @param {string} options.word - The word to define
 * @param {string} options.language - Target language (e.g., "hebrew", "farsi")
 * @param {AbortSignal} [options.signal] - Optional abort signal
 * @returns {Promise<{definition: string, example: string}>}
 */
export async function enrichWord({word, language, signal}) {
  const prompt =
    `Define the ${language} vocabulary word "${word}" in English. ` +
    `Provide a brief definition (1-2 sentences) and one example sentence using the word. ` +
    `Format your response as JSON: {"definition": "...", "example": "..."}`;

  const raw = await ollamaGenerate({prompt, signal});

  try {
    const match = raw.match(/\{[\s\S]*?\}/);
    if (match) {
      return JSON.parse(match[0]);
    }
  } catch (error) {
    // LLM response was not valid JSON — fall through to raw text
  }

  return {definition: raw, example: ''};
}

/**
 * Add a vocabulary word using the ollamaclient API.
 *
 * This function demonstrates how to invoke the nahooray/ollamaclient API
 * to add a word with AI-generated enrichment data. The flow is:
 *
 * 1. Call enrichWord() to get AI-generated definition and example
 * 2. Call the local API to persist the word
 * 3. Optionally store the enrichment data alongside the word
 *
 * @param {object} options
 * @param {string} options.wordEn - English word
 * @param {string} options.wordFa - Word in target language
 * @param {string} options.language - Target language
 * @param {string} [options.transliteration] - Transliteration
 * @param {Function} options.createWordFn - Function to call local API
 * @returns {Promise<object>} The created word with enrichment data
 */
export async function addWordWithEnrichment({
  wordEn,
  wordFa,
  language,
  transliteration,
  createWordFn,
}) {
  let enrichment = {definition: '', example: ''};

  try {
    enrichment = await enrichWord({word: wordEn, language});
  } catch (error) {
    // Enrichment is optional — log and proceed without it
    // eslint-disable-next-line no-console
    console.warn('Ollama enrichment unavailable:', error.message);
  }

  const word = await createWordFn({
    language,
    wordEn,
    wordFa,
    transliteration: transliteration || '',
  });

  return {...word, ...enrichment};
}

function parseJsonArrayFromText(raw) {
  const trimmed = (raw || '').trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    // Continue to bracket extraction fallback.
  }

  const start = trimmed.indexOf('[');
  const end = trimmed.lastIndexOf(']');
  if (start === -1 || end <= start) return [];

  try {
    const parsed = JSON.parse(trimmed.slice(start, end + 1));
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

/**
 * Extract bilingual vocabulary entries from pasted text using Ollama.
 *
 * @param {object} options
 * @param {string} options.text - Pasted source text
 * @param {string} options.language - Target language ("hebrew" or "farsi")
 * @param {number} [options.maxWords] - Max entries to extract (default 20)
 * @param {AbortSignal} [options.signal] - Optional abort signal
 * @returns {Promise<Array<{wordEn: string, wordFa: string, transliteration: string}>>}
 */
export async function extractWordPairsFromText({text, language, maxWords, signal}) {
  const langName = language === 'hebrew' ? 'Hebrew' : 'Farsi';
  const limit = Number.isFinite(maxWords) && maxWords > 0 ? Math.min(maxWords, 60) : 20;
  const prompt =
    `Read the following text and extract up to ${limit} useful vocabulary entries for a learner. ` +
    `Return ONLY a JSON array, with no markdown and no explanation. ` +
    `Each array item must be: {"wordEn":"...","wordFa":"...","transliteration":"..."}. ` +
    `wordEn MUST be English only. ` +
    `wordFa MUST be ${langName} script only. ` +
    `Do not put ${langName} in wordEn and do not put English in wordFa. ` +
    `transliteration should be English letters for wordFa. ` +
    `Do not include duplicates.\n\nText:\n${text}`;

  const raw = await ollamaGenerate({prompt, signal});
  const entries = parseJsonArrayFromText(raw)
      .map((entry) => ({
        wordEn: typeof entry?.wordEn === 'string' ? entry.wordEn.trim() : '',
        wordFa: typeof entry?.wordFa === 'string' ? entry.wordFa.trim() : '',
        transliteration: typeof entry?.transliteration === 'string' ? entry.transliteration.trim() : '',
      }))
      .filter((entry) => entry.wordEn && entry.wordFa);

  if (!entries.length) {
    return [];
  }

  const normalizePrompt =
    `Normalize this JSON array into correct bilingual pairs for ${langName} learners. ` +
    `Return ONLY JSON array with items {"wordEn":"...","wordFa":"...","transliteration":"..."}. ` +
    `Rules: wordEn must be English only; wordFa must be ${langName} only. ` +
    `If values are swapped, swap them. If one side is missing/wrong, translate to fix it. ` +
    `Preserve meaning and keep concise vocabulary entries.\n\nInput JSON:\n` +
    `${JSON.stringify(entries)}`;

  const normalizedRaw = await ollamaGenerate({
    prompt: normalizePrompt,
    signal,
  });

  return parseJsonArrayFromText(normalizedRaw)
      .map((entry) => ({
        wordEn: typeof entry?.wordEn === 'string' ? entry.wordEn.trim() : '',
        wordFa: typeof entry?.wordFa === 'string' ? entry.wordFa.trim() : '',
        transliteration: typeof entry?.transliteration === 'string' ? entry.transliteration.trim() : '',
      }))
      .filter((entry) => entry.wordEn && entry.wordFa);
}

// Long pages are truncated before extraction so the LLM prompt stays small
// enough for local models to handle reliably.
export const MAX_EXTRACT_TEXT_LENGTH = 12000;

/**
 * Extract vocabulary from an already-fetched page payload.
 * The fetch itself is done server-side by WordsApi (`importUrl` in
 * localApi.js) to avoid CORS and unsafe cross-origin reads; this function
 * only runs the Ollama extraction over the returned text.
 *
 * @param {object} options
 * @param {string} options.pageText - Cleaned page text from the import API
 * @param {string} options.language - Target language ("hebrew" or "farsi")
 * @param {number} [options.maxWords] - Max entries to extract
 * @param {AbortSignal} [options.signal]
 */
export async function extractWordPairsFromPage({pageText, language, maxWords, signal}) {
  const text = (pageText || '').length > MAX_EXTRACT_TEXT_LENGTH
    ? pageText.slice(0, MAX_EXTRACT_TEXT_LENGTH)
    : pageText;
  return extractWordPairsFromText({text, language, maxWords, signal});
}
